import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Star,
  Send,
  Check,
  AlertCircle,
  Loader2,
  User,
  Phone,
  Mail,
  MessageSquare,
  UtensilsCrossed,
  Utensils,
  Sparkles,
  Wind,
  BadgeDollarSign,
} from 'lucide-react';
import customerService from '../../services/customerService';

// ─── Star Rating ─────────────────────────────────────────────────────────────
function StarRating({ value, onChange, size = 'lg' }) {
  const [hovered, setHovered] = useState(0);
  const sizeCls = size === 'lg' ? 'w-9 h-9' : 'w-5 h-5';

  return (
    <div className="flex gap-1" role="group" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n !== 1 ? 's' : ''}`}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          className={`${sizeCls} flex items-center justify-center transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 rounded`}
        >
          <Star
            size={size === 'lg' ? 28 : 17}
            className={`transition-colors ${
              n <= (hovered || value)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-slate-100 text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// ─── Sub Rating Row ───────────────────────────────────────────────────────────
function SubRatingRow({ label, icon: Icon, field, value, onChange }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
      <div className="flex items-center gap-1.5 w-24 flex-shrink-0">
        <Icon size={13} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <StarRating value={value} onChange={(v) => onChange(field, v)} size="sm" />
      {value > 0 && (
        <span className="text-xs font-semibold text-amber-600 ml-auto">{value}/5</span>
      )}
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen({ branchName }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center px-4">
      <div className="text-center max-w-xs">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <Check size={36} className="text-emerald-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Thank you!</h1>
        <p className="text-sm text-slate-500">
          Your feedback for{' '}
          <span className="font-semibold text-slate-700">{branchName || 'us'}</span> has been
          submitted. We appreciate you taking the time.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function PublicFeedbackPage() {
  const { branchId } = useParams();
  const branchName = '';

  const OVERALL_LABELS = ['', 'Poor', 'Below average', 'Average', 'Good', 'Excellent!'];

  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    overallRating: 0,
    foodRating: 0,
    serviceRating: 0,
    ambienceRating: 0,
    valueRating: 0,
    reviewText: '',
    feedbackSource: 'MANUAL',
  });

  const [fieldErrors, setFieldErrors] = useState({
    customerName: '',
    customerPhone: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((e) => ({ ...e, [field]: '' }));
    }
  };

  const validate = () => {
    const errors = { customerName: '', customerPhone: '' };
    let valid = true;

    if (!form.customerName.trim()) {
      errors.customerName = 'Name is required';
      valid = false;
    }

    if (!form.customerPhone.trim()) {
      errors.customerPhone = 'Phone number is required';
      valid = false;
    } else if (!/^\+?[\d\s\-()]{7,15}$/.test(form.customerPhone.trim())) {
      errors.customerPhone = 'Enter a valid phone number';
      valid = false;
    }

    setFieldErrors(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    if (!form.overallRating) {
      setError('Please give an overall rating before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const payload = {
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim() || null,
        overallRating: form.overallRating,
        foodRating: form.foodRating || null,
        serviceRating: form.serviceRating || null,
        ambienceRating: form.ambienceRating || null,
        valueRating: form.valueRating || null,
        reviewText: form.reviewText.trim() || null,
        feedbackSource: form.feedbackSource,
      };
      await customerService.submitFeedback(branchId, payload);
      setSubmitted(true);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen branchName={branchName} />;
  }

  const overallColor =
    form.overallRating >= 4
      ? 'text-emerald-600'
      : form.overallRating === 3
      ? 'text-amber-600'
      : form.overallRating > 0
      ? 'text-red-500'
      : 'text-slate-300';

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-slate-50 pb-16">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
            <UtensilsCrossed size={20} className="text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-slate-900 truncate">
              {branchName || 'Share Your Feedback'}
            </h1>
            <p className="text-[11px] text-slate-400">Name and phone are required</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="max-w-lg mx-auto px-4 py-5 space-y-4">

          {/* Error Banner */}
          {error && (
            <div className="flex items-center gap-2.5 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              <AlertCircle size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── About You ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              About you
            </p>

            {/* Name */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <User size={12} />
                Name <span className="text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) => setField('customerName', e.target.value)}
                placeholder="Your name"
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                  fieldErrors.customerName
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-50'
                    : 'border-slate-200 focus:border-teal-400 focus:ring-teal-50'
                }`}
              />
              {fieldErrors.customerName && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={11} />
                  {fieldErrors.customerName}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Phone size={12} />
                Phone <span className="text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="tel"
                value={form.customerPhone}
                onChange={(e) => setField('customerPhone', e.target.value)}
                placeholder="Your phone number"
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                  fieldErrors.customerPhone
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-50'
                    : 'border-slate-200 focus:border-teal-400 focus:ring-teal-50'
                }`}
              />
              {fieldErrors.customerPhone && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={11} />
                  {fieldErrors.customerPhone}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Mail size={12} />
                Email
                <span className="text-slate-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="email"
                value={form.customerEmail}
                onChange={(e) => setField('customerEmail', e.target.value)}
                placeholder="Your email address"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50"
              />
            </div>
          </div>

          {/* ── Overall Rating ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4">
              Overall rating <span className="text-red-400">*</span>
            </p>

            <div className="bg-slate-50 rounded-xl p-4 text-center space-y-2">
              <div className="flex justify-center">
                <StarRating
                  value={form.overallRating}
                  onChange={(v) => setField('overallRating', v)}
                  size="lg"
                />
              </div>
              <p className={`text-2xl font-bold transition-colors ${overallColor}`}>
                {form.overallRating ? `${form.overallRating}.0 / 5` : '—'}
              </p>
              <p className="text-xs text-slate-400 min-h-[16px]">
                {OVERALL_LABELS[form.overallRating] || 'Click a star to rate'}
              </p>
            </div>

            {/* Sub-ratings */}
            <div className="mt-5">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Detailed ratings{' '}
                <span className="font-normal normal-case opacity-70">(optional)</span>
              </p>
              <div className="bg-slate-50 rounded-xl px-4 py-1">
                {[
                  { label: 'Food',     icon: Utensils,        field: 'foodRating'     },
                  { label: 'Service',  icon: Sparkles,        field: 'serviceRating'  },
                  { label: 'Ambience', icon: Wind,            field: 'ambienceRating' },
                  { label: 'Value',    icon: BadgeDollarSign, field: 'valueRating'    },
                ].map(({ label, icon, field }) => (
                  <SubRatingRow
                    key={field}
                    label={label}
                    icon={icon}
                    field={field}
                    value={form[field]}
                    onChange={setField}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ── Review & Source ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <MessageSquare size={12} /> Review comments
                <span className="text-slate-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={form.reviewText}
                onChange={(e) => setField('reviewText', e.target.value)}
                placeholder="Tell us about your experience..."
                rows={3}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 resize-none"
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                <Utensils size={12} /> How did you visit?
              </label>
              <select
                value={form.feedbackSource}
                onChange={(e) => setField('feedbackSource', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 bg-white"
              >
                <option value="MANUAL">In-store / Dine-in</option>
                <option value="TAKEAWAY">Takeaway</option>
                <option value="QR_CODE">QR code</option>
                <option value="ONLINE">Online</option>
                <option value="PHONE">Phone order</option>
              </select>
            </div>
          </div>

          {/* ── Submit ── */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Submitting…</>
            ) : (
              <><Send size={16} /> Submit Feedback</>
            )}
          </button>

          <p className="text-[10px] text-slate-400 text-center">
            Your feedback helps us improve. Thank you!
          </p>
        </div>
      </form>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}